import { getWriteSignedUrl } from "frontend/api";
import { SyncService } from "frontend/services/syncService";
import { useBoardValue } from "frontend/state/board-state";
import { useAtom, useAtomValue } from "jotai";
import Konva from "konva";
import { useTimeout, useUnmount } from "react-use";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { prefixForUserDataInSession } from "shared/datamodel/voting";
import { layerRefAtom, syncServiceAtom } from "state-atoms";
import { summarizeVotes, timeLeftForSession, useActiveVotingSession, votingAtom } from "./voting-common";
import { initArray } from "utils/fn-utils";
import { measureNodesScreenSpace } from "utils/node-utils";

function dataUrlToBlob(dataUrl: string) {
  return fetch(dataUrl).then((res) => res.blob());
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function retryGCSUpload(fetcher: () => ReturnType<typeof fetch>, retries = 3): ReturnType<typeof fetch> {
  async function run(current: number): ReturnType<typeof fetch> {
    let status = 0;
    let statusText: string;

    try {
      const response = await fetcher();
      if (response.ok) {
        return response;
      }
      status = response.status;
      statusText = response.statusText;
    } catch (error) {
      // If the fetch was cancelled (by caller obviously), we don't retry
      if (error instanceof DOMException && (error as DOMException).name == "AbortError") {
        throw error;
      }
      // otherwise, drop down to retry logic
    }

    // see https://cloud.google.com/storage/docs/retry-strategy
    if (current < retries && (status == 408 || status == 429 || (status >= 500 && status < 600))) {
      // see https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
      const temp = Math.min(64_000, 1 * 2 ** current);
      const wait = temp / 2 + (Math.random() * temp) / 2;
      await sleep(wait);
      return run(current + 1);
    }
    throw new Error(`Failed to fetch: ${status} ${statusText!}`);
  }
  return run(0);
}

async function uploadVotes(
  syncService: SyncService<RW> | null,
  konvaLayer: Konva.Layer | null,
  board: any,
  sessionId?: number
) {
  if (sessionId === undefined) {
    return;
  }

  try {
    // wait 2 seconds for all other clients to upload their results to Reflect.
    //FIXME - This is not foolproof. it might take more than 2 seconds. each user should upload his own thumbnails.
    await sleep(2000);

    // Now to upload thumbnails of all elements
    const results = await syncService?.getReplicache()?.query(
      async (tx) =>
        (await tx
          .scan({ prefix: prefixForUserDataInSession(sessionId) })
          .values()
          .toArray()) as any[]
    );
    const ids = Object.keys(summarizeVotes(results ?? []));

    // Get thumbnails from the konva nodes
    const allNodes =
      konvaLayer
        ?.find((node: Konva.Node) => {
          const id = node.id();
          return id && ids.includes(id);
        })
        .toArray() ?? [];

    const rects = measureNodesScreenSpace(allNodes, { skipShadow: true });
    // calculate pixel ratio for each node: I want thumbnails that are 200px on their large side
    const pixelRatios = rects.map(({ width, height }) => 200 / Math.max(width, height));
    // convert all to data urls
    const dataUrls = allNodes.map((node, index) => node.toDataURL({ pixelRatio: pixelRatios[index] }));
    // and save in an object (id -> dataUrl)
    const thumbnails: Record<string, string> = {};
    for (let index = 0; index < dataUrls.length; index++) {
      const id = allNodes[index].id();
      if (id) {
        thumbnails[id] = dataUrls[index];
      }
    }
    const blobsPromise = dataUrls.map(dataUrlToBlob);

    // ask the backend to give us write-signed urls
    const uploadUrls = allNodes.map(
      (node) => `voting-thumbnails/${board.board.accountId}/${board.documentId}/${sessionId}/${node.id()}.png`
    );

    const signedUrls = await Promise.allSettled(uploadUrls.map((url) => getWriteSignedUrl(url, "image/png")));

    // now upload to google-storage, and upon success write the results in replicache
    // or maybe mark in replicache and then upload???
    const blobs = await Promise.all(blobsPromise);

    const uploads = await Promise.all(
      initArray(allNodes.length, async (index: number) => {
        const signedUrl = signedUrls[index];
        const blob = blobs[index];
        if (signedUrl.status === "fulfilled") {
          // start an async function that will upload the blob to the signedUrl, and return true/false
          return retryGCSUpload(() =>
            fetch(signedUrl.value.url, {
              method: signedUrl.value.method,
              body: blob,
              headers: { "Content-Type": "image/png" },
            })
          )
            .then(() => true)
            .catch(() => false);
        }
        return false;
      })
    );

    const urls: Record<string, string> = {};
    for (let index = 0; index < uploads.length; index++) {
      if (uploads[index]) {
        const id = allNodes[index].id();
        urls[id] = uploadUrls[index];
      }
    }

    syncService?.mutate.setThumbnails({
      sessionId,
      urls,
    });
  } catch (error) {
    console.error("Error uploading voting results", error);
  }
}

export function VotingUploader({
  setShowUploadingVotesModal,
}: {
  setShowUploadingVotesModal: (show: boolean) => void;
}) {
  const syncService = useAtomValue(syncServiceAtom);
  const current = useActiveVotingSession(syncService);
  const [{ user }] = useBoardValue();
  const sessionInProgress = current && (timeLeftForSession(current) ?? -1) >= 0;
  const isOwner = user?.id == current?.ownerId;

  const timeLeftInSession = current ? current.start + current.duration * 1000 - Date.now() : -1;
  const refreshTime = sessionInProgress && isOwner && timeLeftInSession > 0 ? timeLeftInSession : undefined;
  useTimeout(refreshTime); // this will refresh the component when voting ends, unmounting the child, triggering effect

  return sessionInProgress && isOwner ? (
    <UploadOnUnmount setShowUploadingVotesModal={setShowUploadingVotesModal} />
  ) : null;
}

function UploadOnUnmount({ setShowUploadingVotesModal }: { setShowUploadingVotesModal: (show: boolean) => void }) {
  const [board] = useBoardValue(); //todo: is board.user and userAtomValue the same ??
  const [state] = useAtom(votingAtom);
  const syncService = useAtomValue(syncServiceAtom);
  const layerRef = useAtomValue(layerRefAtom);

  useUnmount(() => {
    setShowUploadingVotesModal(true);
    const closeModal = () => setShowUploadingVotesModal(false);
    uploadVotes(syncService, layerRef?.current ?? null, board, state?.sessionId).then(closeModal, closeModal);
  });

  return null;
}
